export
const name: string = "LightingCommon";

export
const preproc: string = ``;

export
const declaration: string = `
    // Screen space ambient occlusion
    // ------------------------------
    #ifdef SSAO
    uniform sampler2D   u_ssao_texture;
    uniform vec4        u_view;
    #endif

    // Shadow maps
    // -----------
    // Common
    #if defined(SHADOW_MAP) || defined(SHADOW_MAP_VARIANCE)
    uniform sampler2D   u_shadow_map_texture;
    uniform float       u_shadow_map_intensity;
    #endif

    // PCF
    #ifdef SHADOW_MAP
    uniform vec2 u_shadow_map_size;
    #endif

    // VSM
    #ifdef SHADOW_MAP_VARIANCE
    uniform mat4    u_lightViewMatrix;
    uniform mat4    u_lightProjMatrix;
    uniform float   u_lightMaxLength;
    uniform float   u_lightBleeding;
    #endif

    // FOG
    // -------------
    #ifdef FOG
    uniform       vec3   u_fogColor;
    uniform       float  u_fogDistance;
    uniform       float  u_fogDensity;
    #endif
`;

export
const varyings: string = `
    // PCF, SSAO
    #if defined(SSAO) || defined(SHADOW_MAP)
    varying vec4    v_clip_space_position;
    #endif

    // VSM
    #ifdef SHADOW_MAP_VARIANCE
    varying vec4    v_shadow_map_position;
    #endif
`;

export
const implementation: string = `
    #ifdef SSAO
    /**
     * Convert <0, 1> to <Offset, Size>
     */
    vec2 toViewCoords(in vec2 txCoords, in vec4 viewSize) {
        return txCoords * viewSize.zw + viewSize.xy;
    }
    #endif

    // PCF Shadow Map
    // --------------
    #ifdef SHADOW_MAP
    // Blur some samples
    // This is not good when there is sharp edge (chair in light, wall behind in shadow)
    float getShadow()
    {
        const int samples = 4;
        const float bias = 0.01;

        vec2 texelSize = 1.0 / u_shadow_map_size;
        vec2 position = (v_clip_space_position.xy / v_clip_space_position.w) * 0.5 + 0.5;
        vec3 myPosition = texture2D(u_shadow_map_texture, position).xyz;
        int count = 1;

        float result = myPosition.x;
        for (int i=-samples; i<samples; ++i) {
            for (int a=-samples; a<samples; ++a) {
                vec2 offset = vec2(float(i), float(a)) * texelSize;
                vec3 element = texture2D(u_shadow_map_texture, position + offset).xyz;

                if (abs(myPosition.z -  element.z) > bias) {
                    continue;
                }

                result += element.x;
                ++count;
            }
        }

        float tmp = 1.0 - result / float(count);
        tmp *= u_shadow_map_intensity;
        return 1.0 - tmp;
    }
    #endif

    // VSM Shadow Map
    // --------------
    #ifdef SHADOW_MAP_VARIANCE
    float linstep(float low, float high, float v) {
        return clamp((v-low) / (high - low), 0.0, 1.0);
    }

    float VSM(sampler2D depths, vec2 uv, float compare) {
        if (uv.x >= 1.0 || uv.x <= 0.0 || uv.y >= 1.0 || uv.y <= 0.0) {
            return 1.0;
        }

    #ifdef BIT16
        vec4 data = texture2D(depths, uv);
        vec2 moments = vec2(unpackFloat(vec2(data.x, data.y)), unpackFloat(vec2(data.z, data.w)));
    #else
        vec2 moments = texture2D(depths, uv).xy;
    #endif

        float p = step(compare, moments.x);

        // ..., "VarianceMin");
    #ifdef BIT16
        float variance = max(moments.y - moments.x * moments.x, 0.00025);
    #else
        float variance = max(moments.y - moments.x * moments.x, 0.000001);
    #endif

        float d = compare - moments.x;

        // linstep("LightBleedingReduction", ...
        float p_max = linstep(u_lightBleeding, 1.0, variance / (variance + d*d));

        return clamp(max(p, p_max), 0.0, 1.0);
    }

    float getShadow() {
        vec3 lightPos = (u_lightViewMatrix * v_shadow_map_position).xyz;
        vec4 lightDevice = u_lightProjMatrix * vec4(lightPos, 1.0);
        vec2 lightDeviceNormal = lightDevice.xy / lightDevice.w;
        vec2 lightUV = lightDeviceNormal * 0.5 + 0.5;

        // Shadow calc
        float lightDepth2 = clamp(length(lightPos) / u_lightMaxLength, 0.0, 1.0);
        float shadow = VSM(u_shadow_map_texture, lightUV, lightDepth2);

        float tmp = 1.0 - shadow;
        tmp *= u_shadow_map_intensity;
        return 1.0 - tmp;
    }
    #endif

    #ifdef FOG
    /**
     * Mix final color with fog.
     */
    lowp vec3 applyFog(lowp vec3 rgb)
    {
        const float LOG2 = 1.442695;
        float z = (gl_FragCoord.z / gl_FragCoord.w) / u_fogDistance;
        float fogFactor = exp2( -u_fogDensity * u_fogDensity * z * z * LOG2 );
        fogFactor = clamp(fogFactor, 0.0, 1.0);

        rgb = mix(u_fogColor, rgb, fogFactor);

        return rgb;
    }
    #endif

    /*
    lowp vec4 applyFog2(
                    in lowp vec3   rgb,      // original color of the pixel
                    in float distance, // camera to point distance
                    in lowp vec3  rayOri,   // camera position
                    in lowp vec3  rayDir )  // camera to point vector
    {
        float fogAmount = c * exp(-rayOri.y*b) * (1.0-exp( -distance*rayDir.y*b ))/rayDir.y;
        vec3  fogColor  = vec3(0.5,0.6,0.7);
        return mix( rgb, fogColor, fogAmount );
    }
    */


    #ifdef GAMMA
    /**
     * Apply standart gamma correction to final color.
     */
    lowp vec4 applyGammaCorrection(lowp vec4 finalColor)
    {
        return vec4(pow(abs(finalColor.xyz), vec3(1.0/2.2) ), finalColor.w);
    }
    #endif
`;
